import type { Competitor } from '../types'

// Sources (checked 2026-10-01): beacons.ai/i/pricing (USD, same for EU
// visitors), beacons.ai/i/whats-new, help.beacons.ai articles on fees, custom
// domains, fonts, blocks, analytics, and the support block.
const beacons: Competitor = {
  slug: 'beacons',
  name: 'Beacons',
  tagline: 'All-in-one creator business tool: link in bio, store, email, and media kit.',
  site: 'https://beacons.ai',
  pricingUrl: 'https://beacons.ai/i/pricing',
  checkedOn: '2026-10-01',
  seo: {
    title: 'Beacons alternative: mychar.gg vs Beacons compared (2026)',
    description: 'A Beacons alternative for anyone who cares how their page looks: animated effects, a music player, video clips, and one-time pricing compared with Beacons plans.'
  },
  hero: {
    title: 'The Beacons alternative for a page that looks like you',
    description: 'Beacons runs a creator business. mychar.gg makes the page itself stand out, with a live drag-and-drop editor, effects, music, and clips, for a one-time price.'
  },
  summary: 'mychar.gg is the better Beacons alternative if the look of your page matters more than running a store from it: you get animated backgrounds, custom cursors, a music player, video clips that play on the page, and Discord and Twitch sections, and removing branding costs €6.99 once instead of Beacons Creator Plus at $25 a month. Beacons is the stronger pick for selling, email marketing, and media kits.',
  chooseMychar: [
    'want your page to look designed, with effects, cursors, and your own fonts',
    'want music and video clips that play on the page',
    'want Discord and live Twitch sections',
    'would rather pay once than subscribe'
  ],
  chooseThem: [
    'sell products, courses, or memberships from your bio',
    'want email marketing, a media kit, and Instagram auto-DMs in one account',
    'want detailed analytics free from day one'
  ],
  features: {
    free_plan: { yes: true, text: 'Store with a 9% fee, Beacons branding' },
    paid_plans: 'Creator $8.33, Creator Plus $25 a month',
    billing: 'Monthly or yearly subscription',
    first_year: '$300 (Creator Plus, removes branding)',
    custom_domain: { yes: true, text: 'Creator and up' },
    remove_branding: { yes: true, text: 'Creator Plus and up' },
    editor: 'Block editor',
    themes: { yes: true, text: 'Theme sets and an AI designer' },
    fonts: 'Over 1,000 on every plan',
    custom_fonts: false,
    effects: 'Scroll gradients and link animations',
    cursors: false,
    video_background: { yes: true, text: 'Every plan' },
    custom_code: false,
    links: { yes: true, text: 'Unlimited, any URL' },
    music_player: { yes: false, text: 'Music embeds only' },
    video_clips: { yes: false, text: 'Embeds from YouTube, TikTok, Twitch' },
    comments: false,
    view_counter: { yes: false, text: 'Follower counts instead' },
    qr_code: true,
    youtube: { yes: true, text: 'Auto-updating video block' },
    twitch: 'Stream and clip embeds',
    discord: { yes: false, text: 'No Discord block' },
    music_embeds: { yes: true, text: 'Spotify, SoundCloud, Apple Music' },
    analytics_free: { yes: true, text: 'Views, clicks, sources, lifetime' },
    analytics_live: 'Traffic sources free, no live view listed',
    store: { yes: true, text: 'Every plan, 9% fee below Creator Plus' },
    tips: 'Support block, no Beacons fee',
    email_capture: { yes: true, text: 'With built-in email marketing' },
    scheduling: { yes: true, text: 'Every plan' },
    mobile_app: { yes: true, text: 'iOS and Android' }
  },
  plans: [
    { name: 'Free', price: '$0', period: 'forever', note: 'Link in bio, store with a 9% seller fee, media kit, 50 email sends a month.' },
    { name: 'Creator', price: '$8.33', period: '/month, billed yearly', note: 'Or $10 monthly. Custom domain, 500 email sends, SEO settings.' },
    { name: 'Creator Plus', price: '$25', period: '/month, billed yearly', note: 'Or $30 monthly. 0% seller fees, no branding, pixels, memberships.' },
    { name: 'Creator Max', price: '$900', period: '/year', note: 'Or $100 monthly. Onboarding help and extra business tools.' }
  ],
  plansNote: 'USD. Payment processing fees from Stripe or PayPal apply on top of seller fees.',
  advantages: [
    {
      title: 'Animated backgrounds and cursors',
      description: 'Rain, snow, fire, sakura, plasma, and more, plus custom cursors and trails. Beacons offers scroll gradients and link animations.',
      icon: 'i-lucide-sparkles'
    },
    {
      title: 'Your own music player',
      description: 'Upload tracks that play while visitors look around, on every plan. Beacons embeds players from streaming services.',
      icon: 'i-lucide-music'
    },
    {
      title: 'Video clips that play in place',
      description: 'Upload your own highlights instead of embedding them from other platforms, up to 18 on Elite.',
      icon: 'i-lucide-clapperboard'
    },
    {
      title: 'Discord and live Twitch',
      description: 'Show your Discord profile, a live server card on Elite, and your Twitch live status and schedule. Beacons has no Discord block.',
      icon: 'i-simple-icons-discord'
    },
    {
      title: 'No branding for €6.99, once',
      description: 'Removing branding on Beacons takes Creator Plus at $25 a month. On mychar.gg it comes with Pro for a single payment.',
      icon: 'i-lucide-badge-euro'
    },
    {
      title: 'Extras for creators, streamers, and gamers',
      description: 'Comments, linked friends, achievements, a game library, and a setup section for your gear.',
      icon: 'i-lucide-users'
    }
  ],
  strengths: [
    {
      title: 'A whole creator business in one place',
      description: 'Store, email marketing, a media kit that updates itself, Instagram auto-DMs, and a website builder all live in one Beacons account.'
    },
    {
      title: 'Selling with low fees',
      description: 'Digital products work on every plan, and Creator Plus removes seller fees and adds memberships, courses, and buy now, pay later.'
    },
    {
      title: 'Generous free design and analytics',
      description: 'Video backgrounds, more than 1,000 fonts, link scheduling, and analytics with traffic sources are free on Beacons.'
    },
    {
      title: 'Brand partnerships',
      description: 'Beacons runs an affiliate network and media kit tools aimed at landing brand deals.'
    }
  ],
  sections: [
    {
      title: 'Why people look for a Beacons alternative',
      paragraphs: [
        'Beacons is built around earning: a store, email campaigns, a media kit, and brand deals, with the link page as the front door. That is a great fit when selling is the point, and a lot of tool when it is not. Creators who mostly want a page that looks like them often find the design template-led, and the features they care about sit in the more expensive tiers. Removing the Beacons branding, for example, takes Creator Plus at $25 a month billed yearly.',
        'There are also gaps for some audiences. Beacons has no Discord block, music only plays through embedded streaming players, and video comes from other platforms rather than your own uploads.'
      ]
    },
    {
      title: 'How mychar.gg is different',
      paragraphs: [
        'mychar.gg is built around the page itself. The [editor](/docs/customization) is your page in edit mode: edit text in place, drag sections, links, and widgets where you want them, and choose layouts, fonts, colors, video backgrounds, animated effects, and cursor trails while everything updates live. The music player, video clips, and Discord, YouTube, and Twitch sections put your content on the page instead of behind more links.',
        'Pricing works differently too. There is no subscription: Pro is €6.99 once and Elite €12.99 once, and the only monthly cost is the optional €2.99 custom domain add-on. mychar.gg does not sell products or send emails, so if those are central to your business, Beacons is the better tool, and some creators use both. If you are also weighing Linktree, see the [Linktree comparison](/linktree-alternative).'
      ]
    }
  ],
  switchSteps: [
    { title: 'Claim your username', description: 'Sign up free at [mychar.gg/signup](/signup) and pick the name for mychar.gg/yourname.' },
    { title: 'Move your links', description: 'Add your platforms from about 35 built-in link types, unlimited on every plan. Store and product links count as custom links, which start on Pro.' },
    { title: 'Design the page', description: 'Pick a layout, fonts, and colors, add a background and an effect, or apply a community [skin](/skins).' },
    { title: 'Add media and integrations', description: 'Upload a music track and video clips, and connect Discord, YouTube, or Twitch so those sections update on their own.' },
    { title: 'Update your bios', description: 'Replace your Beacons link everywhere you post. If you keep selling on Beacons, link your Beacons store from your mychar.gg page.' }
  ],
  faq: [
    {
      label: 'Can I sell products on mychar.gg like on Beacons?',
      content: 'No. mychar.gg has no store, checkout, or email tools. You can link to a store anywhere, including a Beacons store, but if selling is your main goal, Beacons fits better.'
    },
    {
      label: 'Is mychar.gg cheaper than Beacons?',
      content: 'For a branding-free page, yes. mychar.gg Pro is €6.99 once. On Beacons, removing branding needs Creator Plus at $25 a month billed yearly. A custom domain is €2.99 per month on mychar.gg and included from Beacons Creator.'
    },
    {
      label: 'Does mychar.gg have analytics?',
      content: 'Yes, on every plan: views, clicks, and top links. Pro adds live visitors, countries, click rate, devices, and referrers, and Elite keeps lifetime history.'
    },
    {
      label: 'Can I use my own domain?',
      content: 'Yes. The custom domain add-on is €2.99 per month on any plan, including Free, with SSL included. See the [custom domain guide](/docs/profile/custom-domain).'
    }
  ]
}

export default beacons
